// AccountantDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  FormControl, InputLabel, MenuItem, Select
} from "@mui/material";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import { Line } from "react-chartjs-2";
import { motion } from "framer-motion";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend
} from "chart.js";
import LoadingSpinner from "../../components/LoadingSpinner";
import { getLoans } from "../../services/loanService";
import { fetchTransactionAudit } from "../../services/transactionAuditService";
import {
  fetchGroupTransactions,
  fetchDeskOfficerNotifications,
} from "../../services/deskOfficerService";
import { fetchGroupTerms } from "../../services/groupService";
import { useNavigate } from "react-router-dom";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const formatCurrency = (value) =>
  typeof value === "number" ? new Intl.NumberFormat("en-TZ").format(value) : value;

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return "";
  const created = new Date(timestamp);
  if (Number.isNaN(created.getTime())) return "";
  const diffMs = Date.now() - created.getTime();
  if (diffMs < 0) return "just now";
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return `${Math.round(diffMs / 1000)}s ago`;
  if (diffMs < hour) return `${Math.round(diffMs / minute)}m ago`;
  if (diffMs < day) return `${Math.round(diffMs / hour)}h ago`;
  return `${Math.round(diffMs / day)}d ago`;
};

const normalizeListResponse = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const getDayKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const buildDayBuckets = (count) => {
  const buckets = [];
  const today = new Date();
  for (let offset = count - 1; offset >= 0; offset -= 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - offset);
    buckets.push({ key: getDayKey(date), label: date.toLocaleDateString("en-TZ", { month: "short", day: "numeric" }) });
  }
  return buckets;
};

const buildDailySeries = (transactions = [], count = 7) => {
  const buckets = buildDayBuckets(count);
  const contributions = buckets.reduce((acc, bucket) => ({ ...acc, [bucket.key]: 0 }), {});
  const disbursements = buckets.reduce((acc, bucket) => ({ ...acc, [bucket.key]: 0 }), {});

  transactions.forEach((tx) => {
    if (!tx?.created_at) return;
    const txDate = new Date(tx.created_at);
    if (Number.isNaN(txDate.getTime())) return;
    const key = getDayKey(txDate);
    if (!Object.prototype.hasOwnProperty.call(contributions, key)) return;
    const amount = Math.abs(Number(tx.amount) || 0);
    if (["MEMBER_CONTRIBUTION", "CONTRIBUTION"].includes(tx.transaction_type)) {
      contributions[key] += amount;
    } else if (["LOAN_DISBURSEMENT", "MEMBER_WITHDRAWAL", "WITHDRAWAL"].includes(tx.transaction_type)) {
      disbursements[key] += amount;
    }
  });

  return {
    labels: buckets.map((bucket) => bucket.label),
    contributions: buckets.map((bucket) => contributions[bucket.key] || 0),
    disbursements: buckets.map((bucket) => disbursements[bucket.key] || 0),
  };
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-TZ", { dateStyle: "medium", timeStyle: "short" });
};

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

const AccountantDashboard = () => {
  const [loans, setLoans] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [auditStatements, setAuditStatements] = useState([]);
  const [programLogs, setProgramLogs] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState("current");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    fetchGroupTerms()
      .then((payload) => {
        if (!active) return;
        setTerms(normalizeListResponse(payload));
      })
      .catch((err) => console.error("Failed to load terms:", err));
    return () => {
      active = false;
    };
  }, []);

  const activeTermId = useMemo(
    () => terms.find((term) => term.status === "ACTIVE")?.id || null,
    [terms]
  );
  const effectiveTermId = useMemo(() => {
    if (selectedTermId === "all") return null;
    if (selectedTermId === "current") return activeTermId || terms[0]?.id || null;
    return selectedTermId;
  }, [selectedTermId, activeTermId, terms]);

  useEffect(() => {
    if (selectedTermId === "current" && !effectiveTermId) {
      return undefined;
    }
    const controller = new AbortController();

    const loadDashboard = async () => {
      setLoading(true);
      try {
        const termFilter = effectiveTermId ? { term_id: effectiveTermId } : {};
        const [loanPayload, transactionPayload, notificationPayload, auditPayload] = await Promise.all([
          getLoans(termFilter),
          fetchGroupTransactions({ ...termFilter, signal: controller.signal }),
          fetchDeskOfficerNotifications({ signal: controller.signal }),
          fetchTransactionAudit(termFilter),
        ]);

        setLoans(normalizeListResponse(loanPayload));
        setTransactions(normalizeListResponse(transactionPayload));
        setNotifications(normalizeListResponse(notificationPayload));
        setAuditStatements(normalizeListResponse(auditPayload?.statements));
        setProgramLogs(normalizeListResponse(auditPayload?.program_logs));
        setError(null);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("AccountantDashboard error:", err);
          setError(err.message || "Failed to load dashboard data");
        }
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
    return () => controller.abort();
  }, [effectiveTermId, selectedTermId]);

  const pendingLoanStatuses = useMemo(
    () => ["PENDING_ACCOUNTANT_APPROVAL", "APPROVED_FOR_DISBURSEMENT"],
    []
  );
  const pendingLoanApprovals = useMemo(
    () =>
      loans
        .filter((loan) => pendingLoanStatuses.includes(loan.status))
        .sort(
          (a, b) =>
            new Date(b.updated_at || b.approved_at || b.created_at).getTime() -
            new Date(a.updated_at || a.approved_at || a.created_at).getTime()
        ),
    [loans, pendingLoanStatuses]
  );

  const collectionsToday = useMemo(() => {
    const todayKey = getDayKey(new Date());
    return transactions
      .filter(
        (tx) =>
          ["MEMBER_CONTRIBUTION", "CONTRIBUTION"].includes(tx.transaction_type) &&
          getDayKey(new Date(tx.created_at)) === todayKey
      )
      .reduce((sum, tx) => sum + Math.abs(Number(tx.amount) || 0), 0);
  }, [transactions]);

  const unreconciledStatements = useMemo(
    () =>
      auditStatements.filter(
        (stmt) =>
          stmt &&
          typeof stmt.balance_after_transaction !== "number" &&
          typeof stmt.balance_after_transaction !== "string"
      ),
    [auditStatements]
  );

  const pendingInvoices = useMemo(() => {
    const invoiceNotifications = notifications.filter((n) =>
      /(invoice|invoices)/i.test(n.text || n.title || "")
    );
    const pendingLogs = programLogs.filter(
      (log) => log.status && log.status.toUpperCase().startsWith("PENDING")
    );
    return invoiceNotifications.length + pendingLogs.length;
  }, [notifications, programLogs]);

  const overview = useMemo(
    () => [
      {
        title: "Pending Loan Approvals",
        value: pendingLoanApprovals.length,
        icon: <PendingActionsIcon sx={{ fontSize: 36, color: "warning.main" }} />,
      },
      {
        title: effectiveTermId ? "Collections (Today, Current Term)" : "Collections (Today)",
        value: collectionsToday,
        icon: <AccountBalanceWalletIcon sx={{ fontSize: 36, color: "primary.main" }} />,
      },
      {
        title: "Unreconciled",
        value: unreconciledStatements.length,
        icon: <HistoryToggleOffIcon sx={{ fontSize: 36, color: "error.main" }} />,
      },
      {
        title: "Pending Invoices",
        value: pendingInvoices,
        icon: <ReceiptLongIcon sx={{ fontSize: 36, color: "success.main" }} />,
      },
    ],
    [pendingLoanApprovals.length, collectionsToday, unreconciledStatements.length, pendingInvoices, effectiveTermId]
  );

  const chartSeries = useMemo(() => buildDailySeries(transactions), [transactions]);
  const chartData = useMemo(
    () => ({
      labels: chartSeries.labels,
      datasets: [
        {
          label: "Collections (TZS)",
          data: chartSeries.contributions,
          borderColor: "#1976d2",
          backgroundColor: "rgba(25,118,210,0.15)",
          tension: 0.3,
          fill: true,
        },
        {
          label: "Disbursements (TZS)",
          data: chartSeries.disbursements,
          borderColor: "#d32f2f",
          backgroundColor: "rgba(211,47,47,0.12)",
          tension: 0.3,
          fill: true,
        },
      ],
    }),
    [chartSeries]
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom" } },
    scales: { y: { beginAtZero: true, ticks: { callback: (value) => formatCurrency(value) } }, x: { grid: { display: false } } },
  };

  const recentEvents = useMemo(() => {
    const events = [];
    notifications.slice(0, 2).forEach((notification) => {
      events.push({
        id: notification.id || notification.pk || `${notification.type}-${notification.created_at}`,
        text: notification.message || notification.text || "Notification",
        time: formatTimeAgo(notification.created_at || notification.timestamp),
      });
    });
    programLogs.slice(0, 3).forEach((log) => {
      events.push({
        id: log.id || `log-${log.created_at}`,
        text: `${log.event_name || "Event"} (${log.status_display || log.status || "update"})`,
        time: formatTimeAgo(log.created_at),
      });
    });
    return events;
  }, [notifications, programLogs]);

  const pendingLoansTable = useMemo(
    () =>
      pendingLoanApprovals.slice(0, 5).map((loan, idx) => ({
        id: loan.id || `loan-${idx}`,
        member: loan.member_name || loan.member_full_name || loan.member?.full_name || "—",
        group: loan.group_name || loan.group?.name || loan.group || "—",
        amount: Number(loan.principal_amount) || Number(loan.amount) || 0,
        submitted: loan.created_at,
        status: loan.status,
      })),
    [pendingLoanApprovals]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          Muhasibu Dashboard
        </Typography>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Term</InputLabel>
          <Select
            value={selectedTermId}
            label="Term"
            onChange={(event) => setSelectedTermId(event.target.value)}
          >
            <MenuItem value="current">Current term</MenuItem>
            <MenuItem value="all">All terms</MenuItem>
            {terms.map((term) => (
              <MenuItem key={term.id} value={term.id}>
                {term.name || `Term ${term.term_number}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <LoadingSpinner />
        </Box>
      )}

      {error && !loading && (
        <Typography color="error" sx={{ mb: 3 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && (
        <>
          <Grid container spacing={3}>
            {overview.map((item, i) => (
              <Grid item xs={12} sm={6} md={3} key={item.title}>
                <motion.div initial="hidden" animate="visible" variants={fadeIn} transition={{ delay: i * 0.12 }}>
                  <Card
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                      boxShadow: 3,
                      borderRadius: 2,
                      "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
                    }}
                  >
                    <Box>{item.icon}</Box>
                    <Box textAlign="right">
                      <Typography variant="h6" fontWeight={700}>
                        {typeof item.value === "number" && item.title.includes("Collections")
                          ? formatCurrency(item.value)
                          : item.value}
                      </Typography>
                      <Typography color="text.secondary">{item.title}</Typography>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={8}>
              <motion.div initial="hidden" animate="visible" variants={fadeIn}>
                <Card sx={{ p: 2, boxShadow: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Collections vs Disbursements (last 7 days)
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <Line data={chartData} options={chartOptions} />
                  </Box>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={4}>
              <motion.div initial="hidden" animate="visible" variants={fadeIn}>
                <Card sx={{ p: 2, boxShadow: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Quick Actions
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Button variant="contained" onClick={() => navigate("/app/loan-approvals")}>
                      Approve All Pending Loans
                    </Button>
                    <Button variant="outlined" onClick={() => navigate("/app/member/special-contributions")}>
                      Approve Special Contribution Withdrawal
                    </Button>
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={7}>
              <motion.div initial="hidden" animate="visible" variants={fadeIn}>
                <Card sx={{ boxShadow: 3 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                      <Typography variant="h6">Pending Loan Approvals</Typography>
                      <Button size="small" variant="contained">
                        View All
                      </Button>
                    </Box>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Member</TableCell>
                            <TableCell>Group</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Submitted</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pendingLoansTable.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6}>
                                <Typography variant="body2" color="text.secondary">
                                  No pending approvals for the accountant.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            pendingLoansTable.map((loan, idx) => (
                              <TableRow key={loan.id}>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>{loan.member}</TableCell>
                                <TableCell>{loan.group}</TableCell>
                                <TableCell>{new Intl.NumberFormat("en-TZ").format(loan.amount)}</TableCell>
                                <TableCell>{formatDateTime(loan.submitted)}</TableCell>
                                <TableCell>{loan.status}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={5}>
              <motion.div initial="hidden" animate="visible" variants={fadeIn}>
                <Card sx={{ boxShadow: 3 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="h6">Unreconciled Transactions</Typography>
                      <Chip label={`${unreconciledStatements.length} items`} size="small" />
                    </Box>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Member</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {unreconciledStatements.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5}>
                                <Typography variant="body2" color="text.secondary">
                                  All transactions reconciled.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            unreconciledStatements.slice(0, 4).map((record, idx) => (
                              <TableRow key={record.id || idx}>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>{record.member_name || record.member || "—"}</TableCell>
                                <TableCell>{record.transaction_type || record.event_name || "—"}</TableCell>
                                <TableCell>{new Intl.NumberFormat("en-TZ").format(record.amount)}</TableCell>
                                <TableCell>{formatDateTime(record.created_at)}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <motion.div initial="hidden" animate="visible" variants={fadeIn}>
                <Card sx={{ boxShadow: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Recent Financial Events (Audit)
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {recentEvents.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No activity has been recorded yet.
                        </Typography>
                      ) : (
                        recentEvents.map((event) => (
                          <motion.div
                            key={event.id}
                            whileHover={{ scale: 1.01 }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "12px",
                              borderRadius: "10px",
                              background: "#f9fafb",
                              border: "1px solid #f0f0f0",
                            }}
                          >
                            <HistoryToggleOffIcon sx={{ color: "#6c0a2b", marginRight: 1 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {event.text}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {event.time}
                              </Typography>
                            </Box>
                          </motion.div>
                        ))
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default AccountantDashboard;
